/**
 * 供应商管理页面 — 采购角色专用
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, Edit, Trash2, Loader2, X } from 'lucide-react';
import { api, getErrorMessage } from '@/services/api';
import { useToast } from '@/components/Toast';
import { useConfirm } from '@/components/ConfirmModal';
import styles from './Users.module.css';

interface Supplier {
  user_id: number; user_name: string; account: string; company: string;
  phone: string; position: string; create_user_name: string; status: number; create_time: string;
}

export default function SupplierManagement() {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('asc');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ user_name:'', account:'', company:'', phone:'', position:'' });

  useEffect(() => { loadSuppliers(); }, []);

  const loadSuppliers = async () => {
    setLoading(true);
    try {
      const res = await api.request<any[]>('/promotions/suppliers');
      setSuppliers((res.data||[]).map((s:any)=>({
        user_id:s.user_id, user_name:s.user_name, account:s.account,
        company:s.company||'', phone:s.phone||'', position:s.position||'', create_user_name:s.create_user_name||'',
        status:s.status, create_time:s.create_time||''
      })));
    } catch (err) { showToast('error', getErrorMessage(err)); }
    finally { setLoading(false); }
  };

  const handleCreate = () => { setEditing(null); setForm({user_name:'',account:'',company:'',phone:'',position:''}); setShowModal(true); };
  const handleEdit = (s: Supplier) => { setEditing(s); setForm({user_name:s.user_name,account:s.account,company:s.company||'',phone:s.phone||'',position:s.position||''}); setShowModal(true); };
  const handleSubmit = async (e: React.FormEvent) => { e.preventDefault();
    if (!form.user_name.trim() || !form.account.trim()) { showToast('warning','请填写用户名和账号'); return; }
    setSaving(true);
    try {
      const body = {user_name:form.user_name,company:form.company,phone:form.phone,position:form.position};
      if (editing) await api.request(`/promotions/suppliers/${editing.user_id}`, { method:'PUT', body:JSON.stringify(body) });
      else await api.request('/promotions/suppliers', { method:'POST', body:JSON.stringify({...form, password:'123456'}) });
      showToast('success', editing?'修改成功':'创建成功'); setShowModal(false); loadSuppliers();
    } catch (err) { showToast('error', getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const handleDelete = async (s: Supplier) => {
    const ok = await confirm({ title:'删除供应商', message:`确定删除供应商「${s.user_name}」吗？\n\n删除后将同时清除该供应商的登录账号、所有档案和文件，此操作不可恢复。`, confirmText:'确认删除', danger:true });
    if (!ok) return;
    try { await api.request(`/promotions/suppliers/${s.user_id}`, { method:'DELETE' }); showToast('success','已删除'); loadSuppliers(); }
    catch (err) { showToast('error', getErrorMessage(err)); }
  };

  const handleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d==='asc'?'desc':'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const SortTh = ({ field, children, cls }: { field: string; children: React.ReactNode; cls?: string }) => (
    <th className={cls || styles.tableHeaderCell} style={{cursor:'pointer',userSelect:'none'}} onClick={() => handleSort(field)}>
      {children} {sortField===field ? (sortDir==='asc'?'▲':'▼') : ''}
    </th>
  );

  let filtered = suppliers.filter(s => {
    if (searchQuery && !s.user_name.includes(searchQuery) && !s.account.includes(searchQuery) && !(s.company||'').includes(searchQuery)) return false;
    return true;
  });

  if (sortField) {
    filtered.sort((a: any, b: any) => {
      let va = a[sortField] ?? '', vb = b[sortField] ?? '';
      const cmp = typeof va==='number' ? va-vb : String(va).localeCompare(String(vb));
      return sortDir==='asc' ? cmp : -cmp;
    });
  }

  const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString('zh-CN') : '-';

  return (
    <div className={styles.container}>
      <div className={styles.header}><h1 className={styles.title}>供应商管理</h1></div>
      <div className={styles.searchBar}>
        <div className={styles.searchInput}>
          <Search size={18} className={styles.searchIcon} strokeWidth={1.5} />
          <input type="text" placeholder="搜索用户名、账号或公司..." value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} />
          {searchQuery && <button className={styles.clearSearch} onClick={()=>setSearchQuery('')}><X size={16}/></button>}
        </div>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead><tr>
            <SortTh field="user_name" cls={styles.tableHeaderCell}>用户名</SortTh>
            <SortTh field="account" cls={styles.tableHeaderCell}>账号</SortTh>
            <SortTh field="company" cls={styles.tableHeaderCell}>公司</SortTh>
            <SortTh field="phone" cls={styles.tableHeaderCell}>联系方式</SortTh>
            <SortTh field="position" cls={styles.tableHeaderCell}>职位</SortTh>
            <SortTh field="create_user_name" cls={styles.tableHeaderCell}>创建用户</SortTh>
            <SortTh field="create_time" cls={`${styles.tableHeaderCell} ${styles.centerCell}`}>创建时间</SortTh>
            <th className={`${styles.tableHeaderCell} ${styles.centerCell}`}>操作</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={8} style={{textAlign:'center',padding:40}}><Loader2 size={20}/></td></tr> :
            filtered.map(s => (
              <motion.tr key={s.user_id} initial={{opacity:0}} animate={{opacity:1}}>
                <td className={styles.tableHeaderCell}><span style={{fontWeight:500}}>{s.user_name}</span></td>
                <td className={styles.tableHeaderCell} style={{color:'var(--ink-500)',fontSize:'.8125rem'}}>{s.account}</td>
                <td className={styles.tableHeaderCell} style={{color:'var(--ink-500)',fontSize:'.8125rem'}}>{s.company || '-'}</td>
                <td className={styles.tableHeaderCell} style={{color:'var(--ink-500)',fontSize:'.8125rem'}}>{s.phone || '-'}</td>
                <td className={styles.tableHeaderCell} style={{color:'var(--ink-500)',fontSize:'.8125rem'}}>{s.position || '-'}</td>
                <td className={styles.tableHeaderCell} style={{color:'var(--ink-500)',fontSize:'.8125rem'}}>{s.create_user_name || '-'}</td>
                <td className={`${styles.tableHeaderCell} ${styles.centerCell}`} style={{fontSize:'.8125rem',color:'var(--ink-500)'}}>{formatDate(s.create_time)}</td>
                <td className={`${styles.tableHeaderCell} ${styles.centerCell}`}>
                  <div className={styles.tableActions}>
                    <button className={styles.tableActionBtn} onClick={()=>handleEdit(s)}><Edit size={14}/></button>
                    <button className={styles.tableActionBtn} onClick={()=>handleDelete(s)}><Trash2 size={14}/></button>
                  </div>
                </td>
              </motion.tr>
            ))}
            {!loading && filtered.length===0 && <tr><td colSpan={8} style={{textAlign:'center',padding:40,color:'#9ca3af'}}>暂无供应商</td></tr>}
          </tbody>
        </table>
      </div>

      <AnimatePresence>
        {showModal && (
          <motion.div className={styles.modalOverlay} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={()=>setShowModal(false)}>
            <motion.div className={styles.modal} initial={{scale:.95}} animate={{scale:1}} exit={{scale:.95}} onClick={e=>e.stopPropagation()}>
              <div className={styles.modalHeader}><h2 className={styles.modalTitle}>{editing?'编辑供应商':'新增供应商'}</h2>
                <button className={styles.modalClose} onClick={()=>setShowModal(false)}><X size={20}/></button></div>
              <form onSubmit={handleSubmit} className={styles.modalBody}>
                <div className={styles.formGroup}><label>用户名 *</label><input value={form.user_name} onChange={e=>setForm({...form,user_name:e.target.value})} required/></div>
                {!editing && <div className={styles.formGroup}><label>账号 *</label><input value={form.account} onChange={e=>setForm({...form,account:e.target.value})} required/></div>}
                <div className={styles.formGroup}><label>公司</label><input value={form.company} onChange={e=>setForm({...form,company:e.target.value})} placeholder="供应商所属公司"/></div>
                <div className={styles.formGroup}><label>联系方式</label><input value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} placeholder="手机号"/></div>
                <div className={styles.formGroup}><label>职位</label><input value={form.position} onChange={e=>setForm({...form,position:e.target.value})} placeholder="如：技术总监"/></div>
                <div className={styles.modalFooter}>
                  <button type="button" onClick={()=>setShowModal(false)} className={styles.cancelButton}>取消</button>
                  <button type="submit" disabled={saving} className={styles.submitButton}>{saving?<Loader2 size={14}/>:'保存'}</button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button className={styles.fab} onClick={handleCreate} whileHover={{scale:1.1}} whileTap={{scale:.9}}>
        <Plus size={28} strokeWidth={2}/>
      </motion.button>
    </div>
  );
}
